import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      // Backend always returns 200 — only network/validation errors land here
      toast.error(err.response?.data?.detail?.[0]?.msg || 'Could not send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050505] flex items-center justify-center px-4 pt-20"
      data-testid="forgot-password-page"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#0A0A0A] to-[#050505]" />
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-[#B5FF00]/5 blur-[100px] rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <span className="font-['Josefin_Sans'] text-3xl font-bold text-[#B5FF00]">UNIGHT</span>
          </Link>
          <h1 className="font-['Josefin_Sans'] text-4xl md:text-5xl font-bold text-white mb-2">
            FORGOT PASSWORD
          </h1>
          <p className="text-zinc-400">
            {sent ? 'Check your inbox' : 'Enter your email and we\u2019ll send you a reset link'}
          </p>
        </div>

        {sent ? (
          <div
            className="border border-[#B5FF00]/40 bg-[#B5FF00]/5 p-6 text-center"
            data-testid="forgot-password-sent"
          >
            <CheckCircle size={36} className="text-[#B5FF00] mx-auto mb-4" />
            <p className="text-white mb-3">
              If an account exists for <span className="text-[#B5FF00]">{email}</span>, a reset link is on its way.
            </p>
            <p className="text-zinc-500 text-sm mb-6">
              The link expires in 1 hour. Don\u2019t see it? Check spam, or try again in a few minutes.
            </p>
            <Link
              to="/login"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="forgot-password-back-to-login"
            >
              <ArrowLeft size={16} />
              BACK TO SIGN IN
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">EMAIL</label>
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 bg-[#0A0A0A] border border-zinc-800 text-white pl-11 pr-4 focus:border-[#B5FF00] focus:outline-none transition-colors"
                  placeholder="your@email.com"
                  required
                  autoFocus
                  data-testid="forgot-password-email-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="forgot-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  SENDING...
                </>
              ) : (
                'SEND RESET LINK'
              )}
            </button>
          </form>
        )}

        <p className="text-center mt-8 text-zinc-500">
          Remembered it?{' '}
          <Link to="/login" className="text-[#B5FF00] hover:underline" data-testid="back-to-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
